import { useState, useEffect } from "react";
import SidePanel from "../../components/SidePanel";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import Spinner from "../../components/Spinner";
import { Link } from "react-router-dom";

const localizer = momentLocalizer(moment);

const CalendarTab = () => {
  const [recipient, setRecipient] = useState([]);
  const [loader, setLoader] = useState(true);
  const [events, setEvents] = useState([]);
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [panelHeading, setPanelHeading] = useState("");
  const token = localStorage.getItem("token");
  const customerID = localStorage.getItem("ID");

  function formatFullName(fullName) {
    if (!fullName) return "";

    // Split the full name into parts
    const nameParts = fullName.trim().split(/\s+/);

    // Check for common prefixes
    const prefixes = ["Mr", "Mrs", "Miss", "Ms", "Dr", "Prof"];
    const hasPrefix = prefixes.includes(nameParts[0]);

    // If a prefix exists, include it and the first name
    if (hasPrefix) {
      return `${nameParts[0]} ${nameParts[1] || ""}`.trim();
    }

    // Otherwise, just return the first name
    return nameParts[0];
  }

  const normalizeEventName = (eventName) => {
    return (
      eventName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "") // Remove non-alphanumeric characters (e.g., apostrophes, spaces)
        .replace(/\s+/g, "") // Remove spaces
        .replace(/^([a-z])/g, (match) => match.toLowerCase()) + "Date"
    ); // Ensure camelCase and add 'Date'
  };

  useEffect(() => {
    const getEvents = async () => {
      try {
        console.log("Fetching events for customerID:", customerID);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/customer/get-customer-recipients/${customerID}`,
          {
            method: "GET",
            headers: {
              "content-type": "application/json",
              "x-sh-auth": token,
            },
          }
        );

        if (!res.ok) {
          throw new Error(`Error fetching data: ${res.statusText}`);
        }

        const data = await res.json();
        const recipientsData = data.data;
        setRecipient(recipientsData);

        console.log("Received recipients data:", recipientsData);

        // Transform the events data into the required format
        const myEventsList = recipientsData.flatMap((recipient) =>
          recipient.eventsId.flatMap((event) => {
            // Handle Birthday and Anniversary separately
            if (event.name === "Birthday") {
              const birthdayDates = Array.from({ length: 50 }, (_, i) =>
                moment(recipient.dob).year(moment().year() + i)
              );
              return birthdayDates.map((date) => ({
                fullName: `${recipient.fullName}'s Birthday`,
                title: `${formatFullName(recipient.fullName)}'s Birthday`,
                _id: `${recipient._id}`,
                start: date.startOf("day").toDate(), // start of the day
                end: date.endOf("day").toDate(), // end of the day
                eventId: event?._id,
              }));
            }

            console.log("recipient: ", recipient);
            if (event.name === "Anniversary") {
              const anniversaryDates = Array.from({ length: 50 }, (_, i) =>
                moment(recipient.anniversaryDate).year(moment().year() + i)
              );
              return anniversaryDates.map((date) => ({
                fullName: `${recipient.fullName}'s Anniversary`,
                title: `${formatFullName(recipient.fullName)}'s Anniversary`,
                _id: `${recipient._id}`,
                start: date.startOf("day").toDate(), // start of the day
                end: date.endOf("day").toDate(), // end of the day
                eventId: event?._id,
              }));
            }

            // Normalize the event name for other events
            const normalizedKey = normalizeEventName(event.name);

            // Function to generate dates dynamically for other events
            const getEventDates = () => {
              if (recipient[normalizedKey]) {
                // Use the corresponding key in recipient for other events
                return Array.from({ length: 50 }, (_, i) =>
                  moment(recipient[normalizedKey]).year(moment().year() + i)
                );
              } else if (event.date) {
                // Fallback to using event.date if no matching key in recipient
                return Array.from({ length: 50 }, (_, i) =>
                  moment(event.date).year(moment().year() + i)
                );
              }
              return []; // Fallback if no dates are available
            };

            // Generate the dates for other events
            const dates = getEventDates();

            // Map each date to the desired event object
            return dates.map((date) => {
              console.log("event: ", event);
              return {
                fullName: `${recipient.fullName}'s ${event.name}`,
                title: `${formatFullName(recipient.fullName)}'s ${event.name}`,
                _id: `${recipient._id}`,
                start: date.startOf("day").toDate(), // start of the day
                end: date.endOf("day").toDate(), // end of the day
                eventId: event?._id,
              };
            });
          })
        );

        // Set the transformed data to the events state
        setEvents(myEventsList);
        setLoader(false);
      } catch (err) {
        console.error("Error fetching events:", err);
        setLoader(false); // Ensure loader stops in case of error
      }
    };

    if (token && customerID) {
      getEvents();
    } else {
      console.error("Missing token or customerID");
      setLoader(false); // Stop loader if token or customerID is missing
    }
  }, [customerID, token]);

  useEffect(() => {
    // Initially display the current month's events
    if (events.length > 0) {
      filterEventsForMonth(new Date());
    } else {
      console.log("No events to display");
      setLoader(false);
    }
  }, [events]);

  const handleDayClick = (date) => {
    const dayEvents = events.filter((event) => {
      return moment(date).isSame(event.start, "day");
    });
    setDisplayedEvents(dayEvents);
    setPanelHeading(moment(date).format("Do of MMMM")); // Example: "3rd of March"
  };

  const handleNavigate = (date, view) => {
    // This function is called when the calendar view changes, including when navigating to "Today"
    if (moment(date).isSame(new Date(), "day") && view === "month") {
      // If navigating to today and the view is a day view, show today's events
      handleDayClick(date);
    } else {
      // Otherwise, filter events for the month
      filterEventsForMonth(date);
    }
  };

  const filterEventsForMonth = (date) => {
    const monthStart = moment(date).startOf("month");
    const monthEnd = moment(date).endOf("month");
    const monthEvents = events.filter((event) => {
      const eventMoment = moment(event.start);
      return eventMoment.isBetween(monthStart, monthEnd, null, "[]");
    });
    setDisplayedEvents(monthEvents);
    setPanelHeading(moment(date).format("MMMM YYYY")); // Example: "March 2024"
  };

  if (loader) {
    return (
      <section className="flex">
        <SidePanel />
        <div className="w-full xl:pl-[20%] lg:pl-10 md:pl-6 sm:pl-4 pl-2 pt-[6rem] px-4">
          <div
            className=" flex items-center justify-center"
            style={{ height: "calc(100vh - 10rem)" }}
          >
            <Spinner props="Loading" size={"80"} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex ">
      <SidePanel />
      <div className="w-full xl:pl-[20%] lg:pl-10 md:pl-6 sm:pl-4 pl-2 pt-[6rem] px-4 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 w-full">
          {/* Smaller Events Panel */}
          <div className="col-span-1 bg-gray-100 shadow rounded-lg p-4 overflow-hidden">
            <h2 className="text-2xl text-gray-800 font-bold mb-4">
              {panelHeading}
            </h2>
            {displayedEvents.length > 0 ? (
              <ul className="space-y-3">
                {displayedEvents.map((event, index) => {
                  // console.log("event: ", event);
                  return (
                    <Link
                      key={index}
                      to={`/dashboard/event_details?title=${event.fullName}&_id=${event?._id}&eventId=${event?.eventId}`}
                      className="block py-2 px-3 rounded-md bg-white shadow-sm transition-shadow duration-200 ease-in-out hover:shadow-md hover:border hover:border-primary hover:shadow-primary text-primary text-sm"
                    >
                      {event.title}
                    </Link>
                  );
                })}
              </ul>
            ) : (
              <p>No events for this period.</p>
            )}
          </div>

          {/* Larger Calendar Section */}
          <div className="col-span-1 md:col-span-3">
            <div className="h-screen">
              <Calendar
                localizer={localizer}
                startAccessor="start"
                endAccessor="end"
                events={events}
                views={["month"]}
                onNavigate={handleNavigate}
                onSelectSlot={({ start }) => handleDayClick(start)}
                selectable={true}
                className="text-xs text-primary cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalendarTab;
